import apiClient from '../index';

export const getStaff = async () => {
  try {
    const response = await apiClient.get('/api/staff/profile');
    return response.data;
  } catch (error) {
    console.error('Error fetching staff:', error);
    throw error;
  }
};

export const getAllStaff = async () => {
  try {
    // Use the correct endpoint that exists on the backend
    const response = await apiClient.get('/api/staff');
    // console.log(
    //   'GetAllStaff API Response:',
    //   JSON.stringify(response.data, null, 2)
    // );

    if (!response.data) {
      console.error('No data received from staff API');
      return [];
    }

    // Handle both array and object responses
    const staffData = response.data.data || response.data;
    if (!Array.isArray(staffData)) {
      console.error('Unexpected response format:', staffData);
      return [];
    }

    return staffData.map((staff) => ({
      id: staff.staffId || staff.id,
      staffId: staff.staffId || staff.id,
      firstName: staff.firstName || '',
      lastName: staff.lastName || '',
      name: `${staff.firstName || ''} ${staff.lastName || ''}`.trim(),
      email: staff.email || '',
      designation: staff.designation || '',
      phone: staff.phoneNumber || '',
      phoneNumber: staff.phoneNumber || '',
      role: staff.Role || 'staff',
      department: staff.department || '',
      isActive: staff.status === 'active',
      verified: staff.verified,
      startDate: staff.startDate || new Date().toISOString(),
      photoUrl: staff.photoUrl || '/placeholder.svg',
      avatar: staff.photoUrl || '/placeholder.svg',
    }));
  } catch (error) {
    console.error('Error fetching all staff:', error);
    console.error('Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
    });
    throw error;
  }
};

export const getStaffById = async (id) => {
  try {
    const response = await apiClient.get(`/api/staff/${id}`);

    if (!response.data) {
      throw new Error('No data received from staff API');
    }

    // Extract staff data from response
    const staffData = response.data.data || response.data;

    // Normalize the data to match the shape used in the UI
    return {
      id: staffData.staffId || staffData.id,
      staffId: staffData.staffId || staffData.id,
      firstName: staffData.firstName || '',
      lastName: staffData.lastName || '',
      name: `${staffData.firstName || ''} ${staffData.lastName || ''}`.trim(),
      email: staffData.email || '',
      phone: staffData.phoneNumber || '',
      phoneNumber: staffData.phoneNumber || '',
      role: staffData.role || 'staff',
      department: staffData.department || '',
      isActive: staffData.status === 'active',
      status: staffData.status || 'inactive',
      joinDate: staffData.createdAt || new Date().toISOString(),
      photoUrl: staffData.photoUrl || '/placeholder.svg',
      avatar: staffData.photoUrl || '/placeholder.svg',
      // Additional fields needed for edit panel
      designation: staffData.designation || '',
      departmentId: staffData.departmentId || '',
      dob: staffData.dob || null,
      bloodGroup: staffData.bloodGroup || '',
      address: staffData.address || '',
      roleId: staffData.roleId || '',
      salary: staffData.salary || '',
      // Keep any extra fields that might be in the response
      ...staffData,
    };
  } catch (error) {
    console.error(`Error fetching staff with id ${id}:`, error);
    throw error;
  }
};

export const createStaff = async (staffData) => {
  try {
    const response = await apiClient.post('/api/staff', staffData);
    return response.data;
  } catch (error) {
    console.error('Error creating staff: ', error);
    throw error;
  }
};

export const createAdminStaff = async (staffData) => {
  try {
    const response = await apiClient.post('/api/staff/admin', staffData);
    return response.data;
  } catch (error) {
    console.error('Error creating admin staff: ', error);
    throw error;
  }
};

export const updateStaff = async (id, staffData) => {
  try {
    const response = await apiClient.put(`/api/staff/${id}`, staffData);
    return response.data;
  } catch (error) {
    console.error(`Error updating staff with id ${id}:`, error);
    throw error;
  }
};

export const deleteStaff = async (id) => {
  try {
    const response = await apiClient.delete(`/api/staff/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting staff with id ${id}:`, error);
    throw error;
  }
};

export const staffLogin = async (body) => {
  try {
    const response = await apiClient.post(`/api/staff/login`, body);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      console.error('Login request timed out. Server might be unavailable.');
      throw new Error(
        'Login request timed out. Please try again or contact support if the issue persists.'
      );
    }
    console.error(`Error on staff login:`, error);
    throw error;
  }
};

export const staffLogout = async () => {
  try {
    const response = await apiClient.post(`/api/staff/logout`);
    return response.data;
  } catch (error) {
    console.error(`Error on staff logout`, error);
    throw error;
  }
};

export const staffVerifyOtp = async (otp) => {
  try {
    const response = await apiClient.post(`/api/staff/verifyemail`, { otp });
    return response.data;
  } catch (error) {
    console.error(`Error on staff verify otp`, error);
    throw error;
  }
};

export const staffResendOtp = async () => {
  try {
    const response = await apiClient.post(`/api/staff/sendemailotp`);
    return response.data;
  } catch (error) {
    console.error(`Error on staff resend otp`, error);
    throw error;
  }
};

export const sendEmailOtp = async () => {
  try {
    console.log('starting OTP call');
    const response = await apiClient.post(`/api/staff/sendemailotp`);
    console.log('response received:', response);
    return response.data;
  } catch (error) {
    console.error(`Error sending initial OTP:`, error);
    throw error;
  }
};

export const createStaffNotification = async (notificationData) => {
  try {
    const response = await apiClient.post('/api/snotify/', notificationData);
    return response.data;
  } catch (error) {
    console.error('Error creating staff notification:', error);
    throw error;
  }
};

export const resetPassword = async ({ token, newPassword }) => {
  try {
    const response = await apiClient.post(`/api/staff/resetpassword/${token}`, {
      newPassword,
    });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      console.error(
        'Password reset request timed out. Server might be unavailable.'
      );
      throw new Error(
        'Password reset request timed out. Please try again or contact support if the issue persists.'
      );
    }
    console.error(`Error on password reset:`, error);
    throw error;
  }
};

export const forgotPassword = async (body) => {
  try {
    const response = await apiClient.post(`/api/staff/forgotpassword`, body);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      console.error('Password request timed out. Server might be unavailable.');
      throw new Error(
        'Password request timed out. Please try again or contact support if the issue persists.'
      );
    }
    console.error(`Error on parent password reset:`, error);
    throw error;
  }
};

export const staffSendEmailChangeOtp = async (newEmail) => {
  try {
    const response = await apiClient.post(`/api/staff/email/sendchangeotp`, {
      newEmail,
    });
    return response.data;
  } catch (error) {
    console.error(`Error on staff send email change otp`, error);
    throw error;
  }
};

export const staffChangeEmail = async (data) => {
  try {
    const response = await apiClient.put(`/api/staff/email/changemail`, data);
    return response.data;
  } catch (error) {
    console.error(`Error on staff change email`, error);
    throw error;
  }
};
